<template>
    <div>
        <BillingPlanSummary
            :isFTC="isFTC"
            :internationalizedPriceWithUnit="internationalizedPriceWithUnit"
            :countryClass="countryClass"
            :pageAttr="pageAttr"
            :plan="plan"
            :isFromDirect="isFromDirect"
            :coupon="coupon"></BillingPlanSummary>
        <BillingPromoSummary
            v-if="showPromoComponent"
            ref="promoSummary"
            :isFTC="isFTC"
            :formConfig="formConfig"
            :displayTrialInfo="displayTrialInfo"
            :orderSummaryConfig="orderSummaryConfig"
            :form="form"
            :coupon="coupon"
            :keydownHandler="handleEnterKey"
            :buttonConfig="buttonConfig"
            :plan="plan"
            :handleClickCouponCTA="handleClickCouponCTA"
            :countryClass="countryClass"
            :internationalizedPrice="internationalizedPrice"
            :pricing="pricing"
            :showSubTotalNow="showSubTotalNow"
            :pageAttr="pageAttr"
            :trialAffectingPricing="trialAffectingPricing"></BillingPromoSummary>
        <BillingPaymentSummary
            :isFTC="isFTC"
            :countryClass="countryClass"
            :disclaimerCopyEl="disclaimerCopyEl"
            :displayTrialInfo="displayTrialInfo"
            :nextBillingDate="nextBillingDate"
            :pageAttr="pageAttr"
            :discountString="coupon && coupon.discountString"
            :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
            :showGiftBalance="showGiftBalance"></BillingPaymentSummary>
        <input
            v-if="plan && plan.recurlyCode"
            type="hidden"
            data-recurly="plan"
            :value="plan.recurlyCode" />
        <input ref="couponRecurly" value="" type="hidden" data-recurly="coupon" />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import InputElm from 'atoms/InputElm';
    import InputField from 'atoms/InputField';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import ButtonCta from 'atoms/ButtonCta';
    import { OrderSummaryConfig } from 'aa/vue/FormConfig';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { DateFormatter, getEndDate } from 'aa/helpers/DateFormatter';
    import Domain from 'helpers/Domain';
    import BillingPlanSummary from 'aa/vue/components/BillingPlanSummary';
    import BillingPromoSummary from 'aa/vue/components/BillingPromoSummary';
    import BillingPaymentSummary from 'aa/vue/components/BillingPaymentSummary';
    import {
        EXP_AB_TEST_INTL_NO_FREE_TRIAL,
        MULTI_SUB_PLAN_PICKER_ENABLED,
        FTC_COMPLIANCE_SWITCHPLAN_ENABLED,
    } from 'aa/helpers/featureConstants';
    import billingInfoMixin from 'aa/vue/components/account/billingInfoMixin';

    export default {
        components: {
            InputElm,
            InputField,
            ButtonCta,
            WaitingOverlay,
            BillingPlanSummary,
            BillingPromoSummary,
            BillingPaymentSummary,
        },
        props: {
            coupon: {
                type: Object,
            },
            couponDisclaimer: {
                type: String,
            },
            couponCheckPending: {
                type: Boolean,
            },
            recurly: {
                type: Object,
            },
            recurlyReady: {
                type: Boolean,
                default: false,
            },
            annualPlanOption: {
                type: Boolean,
                default: true,
            },
            formConfig: {
                type: Object,
                default: null,
            },
            nextNonZeroBillingPrice: {
                type: String,
            },
            pricing: {
                type: Object,
            },
            pageAttr: {
                type: Object,
            },
            couponDiscountBillingDate: {
                type: String,
                default: null,
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
        },

        mixins: [pricePrettyPrint, billingInfoMixin],
        data: function () {
            return {
                isPaymentPage: true,
                form: {},
                countryClass: '',
                internationalizedPrice: null,
                internationalizedZeroPrice: null,
                internationalizedPriceWithUnit: null,
                orderSummaryConfigDefault: OrderSummaryConfig,
            };
        },

        computed: {
            orderSummaryConfig() {
                return this.formConfig || this.orderSummaryConfigDefault;
            },

            ...mapGetters('user', ['isNoTrialUser', 'isSubscriber', 'isExSubscriber']),

            ...mapGetters(['bundleShowtime']),
            ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),

            ...mapGetters(['featureIsActive']),
            ...mapGetters('variant', ['isNoTrialVariant']),
            ...mapGetters('flow', ['isRendezvous']),

            displayTrialInfo() {
                if (this.isABTestNoTrialFeature && this.isNoTrialVariant && !this.isRendezvous) {
                    return '';
                }

                if (this.removeTrial) {
                    return false;
                }
                if (this.isFTC) {
                    return false;
                }
                return this.trialAffectingPricing;
            },

            trialAffectingPricing() {
                return !!this.coupon?.trialString || !!this.plan?.trialString;
            },

            showGiftBalance() {
                return (
                    this.isFTC &&
                    this.isExSubscriber &&
                    this.billingInfo?.recurlyAccountBalance?.value > 0
                );
            },

            removeTrial() {
                // ex-subs without trial extension or coupon not eligible for free trial
                return (
                    Domain.isDomestic() &&
                    ((this.isNoTrialUser && !this.coupon.trialString) || !this.plan?.trial)
                );
            },

            isInternational() {
                return Domain.isInternational();
            },

            isNoTrialUserWithCouponTrial() {
                return this.isNoTrialUser && !!this.coupon.trialString;
            },

            isNoTrialUserWithoutCouponTrial() {
                return this.isNoTrialUser && !this.coupon.trialString;
            },

            hasPromoCode() {
                return this.$store.state.promo == null ? false : true;
            },

            promoSuppressedByShowtimeBundleCopy() {
                if (this.pageAttr.aa_promo_suppressed_message_showtime === undefined) {
                    return '';
                } else {
                    return this.pageAttr.aa_promo_suppressed_message_showtime.replace(
                        '%COUPON_CODE%',
                        this.$store.state.promo,
                    );
                }
            },

            promotionalCode() {
                if (this.hasPromoCode) {
                    return this.$store.state.promo;
                }
                return '';
            },

            plan() {
                const selectedPlan = this.featureIsActive(MULTI_SUB_PLAN_PICKER_ENABLED)
                    ? this.$store.getters['plan/getSelectedPlan']
                    : this.$store.getters['plan/getCurrentPlan'];

                if (selectedPlan?.trialString && this.isNoTrialUser) {
                    selectedPlan.trialString = null;
                }

                return selectedPlan;
            },

            couponTrialEndDate() {
                let discount = !this.coupon.recurlyInfo ? null : this.coupon.recurlyInfo.discount;

                if (!discount || discount.type !== 'free_trial') {
                    return null;
                }

                try {
                    let { amount, unit } = discount.trial;
                    let trialEndDate = getEndDate(amount, unit);
                    return DateFormatter.format(trialEndDate, null, CBS.Registry.region.dateLocale);
                } catch (e) {
                    return null;
                }
            },

            getTodaysDateFormatted() {
                return DateFormatter.format(new Date(), null, CBS.Registry.region.dateLocale);
            },

            nextBillingDate() {
                if (this.couponDiscountBillingDate) {
                    return this.couponDiscountBillingDate;
                }

                if (!this.isFTC) {
                    const nextBillingDate =
                        this.couponTrialEndDate ||
                        this.plan?.next_billing_date ||
                        this.plan?.trialEndDate;

                    this.$emit('trialEndDateFormatted', nextBillingDate);

                    return nextBillingDate;
                }

                // Does this work if the ex-sub is getting a 100% off coupon? The 100% coupon logic is taken care of in calcFullCouponBillingDate
                if (this.isExSubscriber) {
                    return this.plan?.next_billing_date || this.getTodaysDateFormatted;
                }

                if (this.isSwitchPlan && this.featureIsActive(FTC_COMPLIANCE_SWITCHPLAN_ENABLED)) {
                    // We can't use this.plan?.next_billing_date because there will be trial, but for US they don't set trial to false when they don't want it.
                    return this.getTodaysDateFormatted;
                }
            },

            disclaimerCopyEl() {
                let trialEndEl = `<span id="trial-end-date" data-original="${this.plan?.trialEndDate}">${this.plan?.trialEndDate}</span>`;
                let billingSummaryCopy = this.isSubscriber
                    ? this.pageAttr.billing_summary_trial_end_copy_subs
                    : this.pageAttr.billing_summary_trial_end_copy;

                return (billingSummaryCopy || '').replace('%d', trialEndEl).trim();
            },

            buttonConfig() {
                return Object.assign(
                    {
                        displayText:
                            this?.pageAttr?.billing_summary_coupon_field_cta ??
                            this.$getLocale('ok'),
                        event: this.handleClickCouponCTA,
                    },
                    OrderSummaryConfig.buttonConfig,
                );
            },

            showSubTotalNow() {
                return this.removeTrial || this.isInternational;
            },

            showSubTotalNext() {
                return this.removeTrial && !this.isNoTrialUserWithCouponTrial;
            },

            showIntlZeroPrice() {
                return this.removeTrial && this.isNoTrialUserWithCouponTrial;
            },
            showPromoComponent() {
                // Always show during Sign Up flow and Direct Link flow or if FTC Compliance for US is enabled
                if (
                    this.$route.name === 'PAYMENT_SUMMARY' ||
                    this.$route.name === 'DIRECTLINK' ||
                    this.isFTC
                ) {
                    return true;
                }

                // For switch plan flow, only show if minimum term enabled
                return this.pageAttr?.billing_summary_minimum_term_enabled;
            },
            isABTestNoTrialFeature() {
                return this.featureIsActive(EXP_AB_TEST_INTL_NO_FREE_TRIAL);
            },
        },

        methods: {
            getInternationalizedPricesUsingRecurly() {
                if (this.recurly && this.getSelectedPlan) {
                    const currentPlan = this.getSelectedPlan;
                    const { currencySymbol, currencyCode } = this.recurly.Pricing.Checkout();
                    const currentPlanPrice = currentPlan.rawPrice.replace(/,/g, '');

                    const priceLocaleOverride = this.$store.state.serverData?.priceLocaleOverride;
                    const overrideLocale =
                        priceLocaleOverride?.[CBS.Registry.region.property] ??
                        CBS.Registry.region.locale;

                    this.internationalizedZeroPrice = `${this.pricePrettyPrint(0, currencySymbol, currencyCode, overrideLocale)}`;
                    this.internationalizedPrice = `${this.pricePrettyPrint(
                        currentPlanPrice,
                        currencySymbol,
                        currencyCode,
                        overrideLocale,
                    )}`;

                    this.internationalizedPriceWithUnit = `${this.pricePrettyPrint(
                        currentPlanPrice,
                        currencySymbol,
                        currencyCode,
                        overrideLocale,
                    )} / ${currentPlan.priceUnit}`;

                    this.$emit('found-price', {
                        internationalizedPrice: this.internationalizedPrice,
                        internationalizedPriceWithUnit: this.internationalizedPriceWithUnit,
                    });
                }
            },

            handleClickCouponCTA(e) {
                e.preventDefault();
                this.$refs.promoSummary.resetManualCouponField();
                const couponCode = this.attachCurrentCouponToForm();
                if (this.form.couponCode?.value !== null && this.form.couponCode?.value !== '') {
                    this.$refs.couponRecurly.value = couponCode;
                } else {
                    //resetting coupon error message and css class
                    this.resetCouponFieldErrorMessage();
                }

                this.attemptApplyCoupon(couponCode);

                this.getInternationalizedPricesUsingRecurly();
            },

            handleEnterKey(e) {
                if (e.key === 'Enter' || e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();

                    this.handleClickCouponCTA(e);
                }
                this.resetCouponFieldErrorMessage();
            },

            attemptApplyCoupon(couponCode) {
                if (this.plan?.recurlyCode) {
                    this.$emit('applyCoupon', {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    });
                }
            },
            attachCurrentCouponToForm(couponCodeOverride) {
                if (!this.form || !this.form.couponCode) {
                    return '';
                }
                // If couponCodeOverride is provided, use it; otherwise, use the value from the form
                let couponCode = couponCodeOverride
                    ? couponCodeOverride
                    : this.form.couponCode?.value || '';

                if (this.$refs.couponRecurly) {
                    this.$refs.couponRecurly.value = couponCode;
                }

                return couponCode;
            },

            resetCouponFieldErrorMessage() {
                document.querySelector('.field-wrapper--coupon-code').classList.remove('error');
                this.coupon.errorMessage = '';
                this.$emit('resetCoupon');
            },
            ftcUpdateSummaries() {
                let couponCode = '';

                if (this.plan.couponIsValid) {
                    couponCode = this.plan.offerCouponCode ?? '';
                } else {
                    couponCode = this.$refs.couponRecurly.value ?? '';
                }

                this.form.couponCode = couponCode;
                this.attachCurrentCouponToForm(couponCode);

                this.$emit(
                    'updateOrder',
                    {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    },
                    false,
                );
                this.getInternationalizedPricesUsingRecurly();
            },
            updateSummaries() {
                const couponCode = this.attachCurrentCouponToForm();
                if (this.plan) {
                    this.$emit('updateOrder', {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    });
                }
                this.getInternationalizedPricesUsingRecurly();
            },
            processSummaries() {
                if (this.recurlyReady && this.isFTC) {
                    this.ftcUpdateSummaries();
                } else if (!this.isFTC) {
                    this.updateSummaries();
                }
            },
        },

        // Lifecycle Hooks
        beforeCreate: function () {
            Object.assign(OrderSummaryConfig.hiddenInputList.plan, {
                value: this.$store.getters['plan/getSelectedPlan']?.recurlyCode,
            });
        },

        created() {
            this.registrationCountry = this.$store.state.serverData.paymentForm?.country?.value
                ? this.$store.state.serverData.paymentForm?.country?.value.toLowerCase()
                : false;
            this.countryClass = `payment-summary-${this.registrationCountry}`;

            if (this.recurly) {
                if (this.isFTC) {
                    this.processSummaries();
                } else {
                    this.getInternationalizedPricesUsingRecurly();
                }
            }
        },

        watch: {
            recurly(newVal, oldVal) {
                if (oldVal === null && newVal !== null) {
                    this.getInternationalizedPricesUsingRecurly();
                }
            },
            recurlyReady(newVal) {
                if (newVal) {
                    this.processSummaries();
                }
            },

            plan(newVal) {
                this.processSummaries();
            },
            autoPromoChecked(newVal) {
                if (newVal === true) {
                    this.$nextTick(() => {
                        this.attachCurrentCouponToForm(
                            this.orderSummaryConfig?.inputList?.couponCode?.value,
                        );
                    });
                }
            },
        },
    };
</script>
